/**
 * Pantalla de **Actividad de usuarios** (`/analisis/actividad`). Reemplaza el
 * `PlaceholderPage` de `ROUTES.userActivity`. Equivale a `user_activity.html` del
 * SSR (demolido en #61).
 *
 * Exige `advanced_stats` (mismo permiso que la API y el menú del SSR) vía
 * `RequireAdvancedStats`. Orquesta:
 *  - el filtro de período (`PeriodTabs`) que recarga el resumen;
 *  - los KPIs (`ActivityKpis`) y la tabla de usuarios (`ActivityUsersTable`) del
 *    resumen (`useActivitySummary`, cacheado por sede + período);
 *  - el detalle/timeline por usuario en un modal (`ActivityDetailDialog`).
 *
 * Presentacional + hooks; sin `fetch` (`docs/frontend_architecture.md`, Pr. 2).
 * Estados de carga/error contemplados (no pantalla en blanco). La sede activa viaja
 * por `X-Sede-Id` (lib/api), así que el resumen es el de la sede seleccionada.
 */
import { useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { ADVANCED_STATS_PERMISSION, DEFAULT_PERIOD } from "../constants";
import { activityErrorMessage } from "../format";
import { useActivitySummary } from "../hooks/useActivity";
import type { ActivityPeriod } from "../schemas";
import type { ActivityUser } from "../types";
import { ActivityDetailDialog } from "./ActivityDetailDialog";
import { ActivityKpis } from "./ActivityKpis";
import { ActivityUsersTable } from "./ActivityUsersTable";
import { PeriodTabs } from "./PeriodTabs";
import { RequireAdvancedStats } from "./RequireAdvancedStats";

/** Usuario seleccionado para ver su detalle (o `null` si el modal está cerrado). */
type DetailTarget = { username: string; name: string } | null;

export function UserActivityScreen() {
  const { hasPermission } = useAuth();
  const canSee = hasPermission(ADVANCED_STATS_PERMISSION);

  const [period, setPeriod] = useState<ActivityPeriod>(DEFAULT_PERIOD);
  const [detailTarget, setDetailTarget] = useState<DetailTarget>(null);

  // Solo se pide el resumen con el permiso correspondiente (igual que el dashboard
  // con la métrica avanzada): el gating de render lo hace `RequireAdvancedStats`.
  const summaryQuery = useActivitySummary(period, canSee);

  function openDetail(user: ActivityUser) {
    setDetailTarget({ username: user.username, name: user.name });
  }

  return (
    <RequireAdvancedStats>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Actividad de usuarios
            </h1>
            {summaryQuery.isSuccess && summaryQuery.data.start && summaryQuery.data.end ? (
              <p className="mt-1 text-sm text-slate-500">
                {summaryQuery.data.start} → {summaryQuery.data.end}
              </p>
            ) : null}
          </div>
          <PeriodTabs value={period} onChange={setPeriod} />
        </div>

        {summaryQuery.isPending ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando actividad…
              </p>
            </CardContent>
          </Card>
        ) : summaryQuery.isError ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {activityErrorMessage(summaryQuery.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <ActivityKpis summary={summaryQuery.data} />
            <Card>
              <CardHeader>
                <CardTitle>Usuarios</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ActivityUsersTable
                  users={summaryQuery.data.users}
                  onShowDetail={openDetail}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <ActivityDetailDialog
        username={detailTarget?.username ?? null}
        name={detailTarget?.name ?? null}
        period={period}
        onClose={() => setDetailTarget(null)}
      />
    </RequireAdvancedStats>
  );
}
